import {
  getLoincLabDisplayName,
  groupResultRecords,
  HG_OBSERVATION_GROUP_PROFILE,
  type TrendPoint,
} from '@liza/fhir-utils';
import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { CodeableConcept, DocumentReference, Observation, Reference } from '@medplum/fhirtypes';
import { parseISO } from 'date-fns';

export const LOINC_SYSTEM = 'http://loinc.org';

// Relocated to @liza/fhir-utils so DOM-free hosts (patient-native) can share them;
// re-exported here to keep existing web import paths working.
export {
  BP_DIASTOLIC_CODE,
  BP_PANEL_CODE,
  BP_SYSTOLIC_CODE,
  formatVitalValue,
  formatVitalValueParts,
  getObservationIdentifier,
  HG_OBSERVATION_GROUP_PROFILE,
  shouldExcludeLabObservation,
  shouldExcludeObservation,
  toTrendSeries,
  type TrendPoint,
} from '@liza/fhir-utils';

/**
 * The unit to display for a measurement row: a localized `unitOverride` (e.g. a lifestyle metric's
 * Spanish unit) applies only when there's a real reading; a placeholder row with no reading keeps
 * the observation's own `rawUnit` (which is `undefined` → bare em-dash). Shared by the compact and
 * detailed measurement rows so the "override only with a reading" rule lives in one place.
 */
export function displayUnit(
  hasReading: boolean,
  unitOverride: string | undefined,
  rawUnit: string | undefined,
): string | undefined {
  return hasReading ? (unitOverride ?? rawUnit) : rawUnit;
}

/** A trend point scaled for the combined overlay: `n`/`n2` are the 0–1 normalized values; `y`/`y2` are the raw readings. */
export type NormalizedPoint = Omit<TrendPoint, 'y' | 'y2'> & { n: number; n2?: number; y: number; y2?: number };

/**
 * Scale a series' values to 0–1 so series with incompatible units (lbs / mmHg / bpm) can be overlaid
 * on one axis for shape-and-timing comparison. Composite readings (e.g. BP systolic/diastolic) are
 * normalized together over the combined min/max so the two lines keep their relationship. Raw `y`/`y2`
 * are preserved for tooltips. A flat series (single distinct value) maps to the mid-line (0.5).
 */
export const normalizeTrendSeries = (data: TrendPoint[]): NormalizedPoint[] => {
  const values: number[] = [];
  for (const p of data) {
    values.push(p.y);
    if (p.y2 !== undefined) values.push(p.y2);
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const norm = (v: number): number => (span === 0 ? 0.5 : (v - min) / span);
  // Spread the point rather than rebuilding it field by field, so a field added to `TrendPoint`
  // (a reading's own code, its provenance) reaches the chart instead of being silently dropped.
  return data.map((p) => ({ ...p, n: norm(p.y), n2: p.y2 !== undefined ? norm(p.y2) : undefined }));
};

/**
 * The combined time span (epoch-ms) of a set of observations, so several stacked charts can share one
 * x-domain instead of each auto-scaling to its own range. Returns null when there are no dated points.
 * The span is padded by ~2% on each side (a minimum of half a day when all points share one timestamp)
 * so endpoints don't sit flush against the axis edge.
 */
export const getMeasurementsTimeRange = (
  observations: WithId<Observation>[],
): { start: number; end: number } | null => {
  const times = observations.map((o) => parseISO(o.effectiveDateTime ?? '').getTime()).filter((t) => !isNaN(t));
  if (times.length === 0) return null;
  const min = Math.min(...times);
  const max = Math.max(...times);
  const pad = Math.max((max - min) * 0.02, 12 * 60 * 60 * 1000);
  return { start: min - pad, end: max + pad };
};

/**
 * Labels for the two series of a composite observation (e.g. systolic / diastolic on a blood-pressure
 * panel), taken from the component code displays. Returns undefined for single-value observations or
 * when displays are missing, so callers can omit `seriesLabels`.
 */
export const getComponentSeriesLabels = (observation?: Observation): { y: string; y2: string } | undefined => {
  // Only composite readings whose value lives *in* the components (blood pressure) get component
  // series labels. A panel with a top-level scalar plus supplementary components (a sleep night: total
  // duration + stage breakdown) plots its top-level value as one line — `parseTrendValues` ignores the
  // components there — so labeling it as a two-series chart would mislabel that single line.
  if (observation?.valueQuantity?.value !== undefined) return undefined;
  const components = observation?.component;
  if (!components || components.length < 2) return undefined;
  const labelOf = (component: NonNullable<Observation['component']>[number]): string | undefined =>
    component.code?.text ?? component.code?.coding?.find((c) => c.display)?.display;
  const y = labelOf(components[0]!);
  const y2 = labelOf(components[1]!);
  return y && y2 ? { y, y2 } : undefined;
};

/** Role of an Observation within a lab-result tree — drives whether/how it renders. */
export type ObservationRole = 'group' | 'document' | 'result';

/** The `DocumentReference` an Observation points at via `derivedFrom[]`, if any (e.g. an HG PDF report). */
export const getObservationDocumentReference = (obs: Observation): Reference<DocumentReference> | undefined =>
  obs.derivedFrom?.find((ref) => ref.reference?.startsWith('DocumentReference/')) as
    Reference<DocumentReference> | undefined;

/**
 * Classify an Observation so callers can decide how to treat it:
 * - `group`    — a Health Gorilla container wrapper (carries the `hg-observaton-group` profile and
 *                no result of its own). Never render as a result row; descend into its members.
 * - `document` — a leaf that points at a `DocumentReference` (a report PDF) and carries no LOINC code.
 *                HG's "Clinical PDF Report" leaf has a `valueString` ("LABCORP"), so we can't use the
 *                presence of a value to tell it apart from a result — the LOINC code is the signal.
 * - `result`   — a real lab result (LOINC-coded); this is what the labs list shows.
 *
 * Keying `document` on "has a DocumentReference AND no LOINC" means a real LOINC-coded result that
 * additionally references a source document still classifies as `result` and is never filtered out.
 *
 * Scoped to HG's profiled wrappers only — a value-less panel *parent* from a non-HG source (e.g. a
 * Fasten/Metriport-imported CBC header) is intentionally NOT treated as a group, so it keeps
 * rendering as it did before this classifier existed.
 */
export const classifyObservation = (obs: Observation): ObservationRole => {
  if (obs.meta?.profile?.includes(HG_OBSERVATION_GROUP_PROFILE)) return 'group';
  const hasLoinc = obs.code?.coding?.some((c) => c.system === LOINC_SYSTEM && !!c.code) ?? false;
  if (getObservationDocumentReference(obs) && !hasLoinc) return 'document';
  return 'result';
};

/** Extract the first reference range as a displayable string. */
export const extractReferenceRange = (observation: Observation): string | null => {
  if (!observation.referenceRange) {
    return null;
  }

  const refRange = observation.referenceRange[0];
  if (refRange?.text) {
    return refRange.text;
  }

  if (refRange?.low || refRange?.high) {
    const parts: string[] = [];
    if (refRange.low) {
      parts.push(`${refRange.low.value}${refRange.low.unit ? ` ${refRange.low.unit}` : ''}`);
    }
    if (refRange.high) {
      if (parts.length > 0) parts.push('-');
      parts.push(`${refRange.high.value}${refRange.high.unit ? ` ${refRange.high.unit}` : ''}`);
    }
    return parts.join(' ');
  }

  return null;
};

/**
 * Patient-friendly display name for a lab/observation code. Returns the curated English label for
 * known LOINC codes, falling back to the stored display via `formatCodeableConcept`.
 * Render-time only — never rewrites the stored code.
 * For language-aware display, use `displayFor` from `useCodeDisplay` instead.
 */
export const getLabDisplayName = (code: CodeableConcept | undefined): string => {
  const loincCode = code?.coding?.find((c) => c.system === LOINC_SYSTEM)?.code;
  const friendly = loincCode ? getLoincLabDisplayName(loincCode) : undefined;
  return friendly ?? formatCodeableConcept(code) ?? '';
};

/** Extract numeric low/high from the first reference range (for chart ReferenceArea). */
export const extractNumericReferenceRange = (
  observation: Observation,
): { low: number | null; high: number | null } | null => {
  const ref = observation.referenceRange?.[0];
  if (!ref) return null;
  const low = ref.low?.value ?? null;
  const high = ref.high?.value ?? null;
  if (low === null && high === null) return null;
  return { low, high };
};

type GroupedVital = {
  identifier: string;
  name: string;
  results: WithId<Observation>[];
  latest: WithId<Observation>;
};

/**
 * Surface `component` values as standalone observations for any component code the view renders as its
 * own measurement. Sleep stores its stage durations (deep/light/REM) as components of one nightly panel
 * Observation, but the care plan models each stage as a separate measurement/Goal; grouping keys on the
 * top-level code, so without this the stage rows never match an observation and render an em-dash.
 *
 * Each matching component becomes a synthetic Observation carrying that component as its top-level
 * value (id suffixed so it stays distinct), preserving `effectiveDateTime` so trends and "latest"
 * resolve per night. Scoped to `measurementCodes` — the codes this view actually lists — so panels
 * whose components aren't standalone measurements (blood pressure's systolic/diastolic) are untouched.
 * A component whose code equals the panel's own top-level code is left to the real Observation.
 */
export const expandComponentMeasurements = (
  observations: WithId<Observation>[],
  measurementCodes: ReadonlySet<string> | undefined,
): WithId<Observation>[] => {
  if (!measurementCodes?.size) return observations;
  const expanded: WithId<Observation>[] = [];
  for (const obs of observations) {
    expanded.push(obs);
    const topLevelCodes = new Set(obs.code?.coding?.map((c) => c.code));
    for (const component of obs.component ?? []) {
      const coding = component.code?.coding?.find(
        (c) => c.code !== undefined && measurementCodes.has(c.code) && !topLevelCodes.has(c.code),
      );
      if (!coding || !component.valueQuantity) continue;
      expanded.push({
        ...obs,
        id: `${obs.id}::${coding.code}`,
        code: component.code!,
        valueQuantity: component.valueQuantity,
        component: undefined,
      });
    }
  }
  return expanded;
};

/**
 * Group readings into the shape the web measurement/records lists render. Grouping itself lives in
 * `@liza/fhir-utils` (`groupResultRecords`), which is DOM-free and shared with native; this adds the
 * display name, which is a web concern. Administrative rows land in the grouper's `hidden` bucket and
 * are dropped here — none of these lists ever rendered them.
 */
export const groupObservations = (
  observations: WithId<Observation>[],
  preferredLoincs?: ReadonlySet<string>,
): GroupedVital[] =>
  groupResultRecords(observations, preferredLoincs).groups.map((group) => ({
    identifier: group.key,
    // `getLabDisplayName` falls back to `formatCodeableConcept` for non-lab / uncurated codes, so
    // vitals and surveys are unchanged while curated labs get their friendly label here — callers can
    // render `group.name` directly without a separate override.
    name: getLabDisplayName(group.latest.code),
    results: group.records,
    latest: group.latest,
  }));
